import React, { useState } from "react";
import axios from "axios";
import {
  Avatar,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { blue } from "@mui/material/colors";
import { keyframes } from "@emotion/react";

const BASE_URL = "https://server-9p4hx.kinsta.app";
const WOO_LOCATION_META_KEY = "_luxura_location";
const SUMO_PLAN_MAP = {
  "1 Rata": ["10956"],
  "2 Rate": ["10956", "10957"],
  "3 Rate": ["10956", "10957", "10969"],
  "1 Rata Speciala": ["106724"],
};
const DESCRIERE_TOGGLE_REGEX =
  /(\[vc_toggle\s+title=("|')DESCRIERE\2[\s\S]*?\])([\s\S]*?)(\s*\[\/vc_toggle\])/i;

const MARKETING_TITLE_PHRASES = [
  "NEW COLLECTION",
  "NEW",
  "HARD TO FIND",
  "HARD-TO-FIND",
  "BEST DEALS",
  "BEST DEAL",
];

const normalizeBrandToken = (value = "") =>
  String(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9& ]/g, "")
    .trim();

const BRAND_DICTIONARY = [
  "HERMES",
  "HERMÈS",
  "DIOR",
  "CHANEL",
  "GUCCI",
  "ADIDAS",
  "ALEXANDER MCQUEEN",
  "AQUAZZURA",
  "BALENCIAGA",
  "BALMAIN",
  "BOTTEGA VENETA",
  "BOUCHERON",
  "BRIONI",
  "BURBERRY",
  "BUSCEMI",
  "CARTIER",
  "CASADEI",
  "CHLOE",
  "CHOPARD",
  "CHRISTIAN LOUBOUTIN",
  "DAVIDOFF",
  "ELISABETTA FRANCHI",
  "ERMANNO SCERVINO",
  "FENDI",
  "GIUSEPPE ZANOTTI",
  "GIVENCHY",
  "HERMES PARIS",
  "JACQUEMUS",
  "JUST CAVALLI",
  "JW ANDERSON",
  "LE SILLA",
  "LOEWE",
  "LOUIS VUITTON",
  "MANOLO BLAHNIK",
  "MAX MARA",
  "MCM",
  "MIU MIU",
  "MONCLER",
  "MOSCHINO",
  "POMELLATO",
  "PRADA",
  "SAINT LAURENT",
  "YVES SAINT LAURENT",
  "S.T. DUPONT",
  "TAG HEUER",
  "TISSOT",
  "TOM FORD",
  "VALENTINO",
  "VERSACE",
  "VLTN",
  "ZIMMERMANN",
  "ADIDAS X GUCCI",
  "ALAIA",
  "ALEXANDER WANG",
  "AMINA MUADDI",
  "ANN DEMEULEMEESTER",
  "ANTONIO BERARDI",
  "ARMANI",
  "ASTRID ANDERSEN",
  "BALENCIAGA X GUCCI",
  "BOTTEGA",
  "BRUNELLO CUCINELLI",
  "BVLGARI",
  "CAROLINA HERRERA",
  "CELINE",
  "CESARE PACIOTTI",
  "CHRISTIAN DIOR",
  "COMME DES GARCONS",
  "DELVAUX",
  "DOLCE & GABBANA",
  "DSQUARED2",
  "ETRO",
  "GIANVITO ROSSI",
  "GIVENCHY PARIS",
  "GOLDEN GOOSE",
  "RUSLAN BAGINSKIY",
  "HARRY WINSTON",
  "JIMMY CHOO",
  "KARL LAGERFELD",
  "LANVIN",
  "LOUIS VUITTON X YAYOI KUSAMA",
  "LUXURA",
  "MARC JACOBS",
  "MICHAEL KORS",
  "NICHOLAS KIRKWOOD",
  "OFF-WHITE",
  "PHILIPP PLEIN",
  "PIAGET",
  "ROGER VIVIER",
  "SALVATORE FERRAGAMO",
  "STELLA MCCARTNEY",
  "STONE ISLAND",
  "THE ROW",
  "TIFFANY & CO",
  "TOD'S",
  "VAN CLEEF & ARPELS",
  "VEJA",
  "VIKTOR & ROLF",
  "YSL",
].sort((a, b) => b.length - a.length);

const normalizeText = (text = "") =>
  text
    ? String(text)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
    : "";

const stripHtmlTags = (value = "") =>
  String(value)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeSimple = (value = "") => normalizeText(value);

const parseLoosePriceNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const raw = String(value).trim();
  if (!raw) return 0;
  const compact = raw.replace(/\s+/g, "");
  // Handle formats like "2.780", "2,780", "2780", "2.780,50", "2,780.50"
  const hasDot = compact.includes(".");
  const hasComma = compact.includes(",");
  let normalized = compact;
  if (hasDot && hasComma) {
    const lastDot = compact.lastIndexOf(".");
    const lastComma = compact.lastIndexOf(",");
    if (lastComma > lastDot) {
      // 2.780,50 -> remove thousands dots, decimal comma -> dot
      normalized = compact.replace(/\./g, "").replace(",", ".");
    } else {
      // 2,780.50 -> remove thousands commas
      normalized = compact.replace(/,/g, "");
    }
  } else if (hasComma) {
    normalized = compact.replace(",", ".");
  } else if (hasDot) {
    const parts = compact.split(".");
    if (parts.length === 2 && parts[1].length === 3) {
      // 2.780 (thousands separator)
      normalized = parts[0] + parts[1];
    }
  }
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
};

const normalizedMatchesToken = (normalizedSource, token) => {
  if (!normalizedSource) {
    return false;
  }
  const normalizedTarget = normalizeSimple(token);
  if (!normalizedTarget) {
    return false;
  }
  return (
    normalizedSource === normalizedTarget ||
    normalizedSource.includes(normalizedTarget)
  );
};

const deriveLayawayStateFromMeta = (metaData = []) => {
  const enableEntry = metaData.find(
    (item) => item?.key === "_sumo_pp_enable_sumopaymentplans",
  );
  const selectedEntry = metaData.find(
    (item) => item?.key === "_sumo_pp_selected_plans",
  );
  const enabled = enableEntry?.value === "yes";
  const selectedIds = Array.isArray(selectedEntry?.value?.col_1)
    ? selectedEntry.value.col_1
    : [];
  const planKey = Object.keys(SUMO_PLAN_MAP).find((key) => {
    const ids = SUMO_PLAN_MAP[key];
    if (ids.length !== selectedIds.length) {
      return false;
    }
    return ids.every((id, idx) => id === selectedIds[idx]);
  });
  return { enabled, plan: planKey || "" };
};

const extractDescriereBlock = (html = "") => {
  if (!html) return "";
  const match = DESCRIERE_TOGGLE_REGEX.exec(html);
  if (match) {
    return (match[3] || "").trim();
  }
  return html.trim();
};

const buildWooDetailMap = (htmlString = "") => {
  const detailMap = {};
  if (!htmlString || typeof window === "undefined" || !window.DOMParser) {
    return detailMap;
  }
  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const groups = doc.querySelectorAll(".pdp__section-group");
    groups.forEach((group) => {
      const label = group
        .querySelector(".pdp__group-title")
        ?.textContent?.trim();
      const valueNode = group.querySelector(".pdp__group-item");
      if (!label || !valueNode) return;
      const combined = valueNode.textContent
        ? valueNode.textContent
            .split("\n")
            .map((part) => part.replace(/\s+/g, " ").trim())
            .filter(Boolean)
            .join("\n")
        : "";
      if (combined) {
        detailMap[label] = combined;
      }
    });
    const dimensionLabelMap = {
      marime: "Mărime",
      bust: "Bust",
      maneca: "Mânecă",
      umar: "Umăr",
      lungime: "Lungime",
      inaltime: "Înălțime",
      adancime: "Adâncime",
      latime: "Lățime",
      circumferinta: "Circumferință",
      "observatii marime": "Observații mărime",
    };
    const dimensionRegex =
      /(M[ăa]rime|Observații mărime|Observatii marime|Bust|M[âa]nec[ăa]|Um[ăa]r|Lungime|Înălțime|Inaltime|Adâncime|Adancime|Lățime|Latime|Circumferință|Circumferinta)\s*:\s*([^<\n]+)/gi;
    let match;
    while ((match = dimensionRegex.exec(htmlString))) {
      const rawLabel = (match[1] || "").trim();
      const normalizedKey = rawLabel
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      const canonicalLabel = dimensionLabelMap[normalizedKey] || rawLabel;
      const value = (match[2] || "").trim();
      if (value) {
        detailMap[canonicalLabel] = value;
      }
    }
  } catch (error) {
    console.error("Failed to parse WooCommerce description:", error);
  }
  return detailMap;
};

const normalizeDimensionLabel = (label = "") =>
  String(label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const parseDimensionSegments = (raw = "") => {
  const result = {};
  if (!raw) {
    return result;
  }
  const cleaned = String(raw || "")
    .replace(/\|\|/g, "/")
    .replace(/\r/g, "\n")
    .replace(/\n/g, "/");
  const segments = cleaned
    .split("/")
    .map((segment) => segment.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  segments.forEach((segment) => {
    if (!segment) return;
    const colonIndex = segment.indexOf(":");
    if (colonIndex === -1) {
      if (!result.size) {
        result.size = segment.trim();
      } else if (!result.fallback) {
        result.fallback = segment.trim();
      }
      return;
    }
    const label = segment.slice(0, colonIndex);
    const value = segment.slice(colonIndex + 1).trim();
    if (!value) return;
    const normalized = normalizeDimensionLabel(label);
    switch (normalized) {
      case "marime":
      case "marime dimensiune":
      case "size":
        result.size = value;
        break;
      case "bust":
        result.bust = value;
        break;
      case "maneca":
      case "manecă":
      case "mâneca":
      case "mânecă":
        result.sleeve = value;
        break;
      case "umar":
      case "umăr":
        result.shoulder = value;
        break;
      case "lungime":
        result.length = value;
        break;
      case "inaltime":
      case "inalt":
      case "înaltime":
      case "înălțime":
        result.height = value;
        break;
      case "adancime":
      case "adâncime":
        result.depth = value;
        break;
      case "latime":
      case "lățime":
      case "lat":
        result.width = value;
        break;
      case "circumferinta":
      case "circumferință":
        result.circumference = value;
        break;
      case "observatiimarime":
      case "observatii marime":
      case "observatiimarimi":
      case "observatii marimi":
      case "observatiimărime":
      case "observații mărime":
        result.sizeObservation = value;
        break;
      default:
        if (!result.extra) {
          result.extra = {};
        }
        result.extra[normalized] = value;
        break;
    }
  });

  return result;
};

const getWooAttributeOptions = (product, attributeName) => {
  const lowerName = (attributeName || "").toLowerCase();
  const attr =
    product?.attributes?.find((item) => {
      const attrName = (item.name || "").toLowerCase();
      const attrSlug = (item.slug || "").toLowerCase();
      return attrName === lowerName || attrSlug === lowerName;
    }) || null;
  return Array.isArray(attr?.options) ? attr.options : [];
};

const removeMarketingPhrases = (text) => {
  if (!text) return "";
  let result = text;
  MARKETING_TITLE_PHRASES.forEach((phrase) => {
    const regex = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, "gi");
    result = result.replace(regex, " ");
  });
  return result;
};

const sanitizeProductTitleForGenerator = (title) => {
  const stripped = stripHtmlTags(title || "");
  const withoutMarketing = removeMarketingPhrases(stripped);
  return withoutMarketing.replace(/\s+/g, " ").trim();
};

const isMarketingWord = (value) => {
  if (!value) return false;
  const upper = value.toUpperCase().trim();
  return MARKETING_TITLE_PHRASES.includes(upper);
};

const extractAccessoryList = (tags = []) =>
  (Array.isArray(tags) ? tags : [])
    .map((tag) => {
      if (typeof tag === "string") return tag;
      if (typeof tag === "object") {
        return tag?.value || tag?.label || tag?.name || "";
      }
      return "";
    })
    .filter(Boolean)
    .join(", ");

const splitMultilineValues = (value = "") =>
  String(value || "")
    .split("\n")
    .map((entry) => stripHtmlTags(entry).trim())
    .filter(Boolean);

const stripLabelPrefixes = (value = "", labels = []) => {
  const text = stripHtmlTags(value || "");
  if (!text) return "";
  const lower = text.toLowerCase();
  for (const label of labels) {
    const candidate = String(label || "");
    if (!candidate) continue;
    if (lower.startsWith(candidate.toLowerCase())) {
      return text.slice(candidate.length).replace(/^[:\s-]+/, "").trim();
    }
  }
  return text.trim();
};

const removeTrailingCm = (value = "") =>
  String(value || "").replace(/\s*cm$/i, "").trim();

const buildWooDescriptionPayload = (
  details,
  condition,
  selectedTags = [],
  manualDescription = "",
  options = {}
) => {
  if (!details) return null;
  const normalizedCategory = normalizeText(
    options.categoryLabel || details.categorieProd || ""
  );
  const normalizedSubcategory = normalizeText(details.subcategorie || "");
  const categoryMatches = (needle) => {
    const normalizedNeedle = normalizeText(needle);
    if (!normalizedNeedle) {
      return false;
    }
    return (
      normalizedCategory === normalizedNeedle ||
      normalizedCategory.includes(normalizedNeedle)
    );
  };
  const isFootwearCategory = categoryMatches("incaltaminte");
  const isApparelCategory = categoryMatches("imbracaminte");
  const { isAccessoryScarf = false, isAccessoryHat = false } = options || {};
  const isAccessoryWrap =
    isAccessoryScarf || isAccessoryHat || isApparelCategory;
  const formatDimensionValue = (value) => {
    if (value === null || value === undefined) return "";
    const raw = stripHtmlTags(String(value));
    if (!raw) return "";
    return /cm$/i.test(raw) ? raw : `${raw} cm`;
  };

  const rawBrand = sanitizeProductTitleForGenerator(details.brand || "");
  const brand = isMarketingWord(rawBrand) ? "" : rawBrand;
  const model = sanitizeProductTitleForGenerator(details.model || "");
  const sizeSource = isFootwearCategory
    ? details.lungime || ""
    : details.marimeDimensiune || details.lungime || "";
  const rawBaseSize = stripHtmlTags(sizeSource);
  const FOOTWEAR_SIZE_LABELS = [
    "Mărime încălțăminte",
    "Marime incaltaminte",
    "Mărime",
    "Marime",
    "Size",
  ];
  const FOOTWEAR_OBS_LABELS = [
    "Observații mărime",
    "Observatii marime",
    "Observații marimi",
    "Observatii marimi",
  ];
  const sanitizeFootwearSize = (value = "") =>
    removeTrailingCm(stripLabelPrefixes(value, FOOTWEAR_SIZE_LABELS));
  const sanitizeFootwearObservation = (value = "") =>
    stripLabelPrefixes(value, [...FOOTWEAR_OBS_LABELS, ...FOOTWEAR_SIZE_LABELS]);
  const baseSize = isFootwearCategory
    ? sanitizeFootwearSize(rawBaseSize)
    : rawBaseSize;
  const includeInteriorColor = !isAccessoryWrap;
  const includeInteriorMaterial = !isAccessoryWrap;
  const includeDepthMeasurement = !isAccessoryWrap && !isFootwearCategory;
  const includeHardware = !isAccessoryWrap;
  const isBagCategory =
    categoryMatches("genti") || categoryMatches("geanta");

  const lengthValue = isFootwearCategory
    ? sanitizeFootwearSize(details.lungime || rawBaseSize)
    : formatDimensionValue(details.lungime || "");
  const heightValue = formatDimensionValue(details.inaltime || "");
  const depthValue = includeDepthMeasurement
    ? formatDimensionValue(details.adancime || "")
    : "";
  const color = stripHtmlTags(
    [
      details.culoareExterioara,
      includeInteriorColor ? details.culoareInterioara : "",
    ]
      .filter(Boolean)
      .join(", ")
  );
  const exteriorMaterials = splitMultilineValues(details.materialExterior);
  const interiorMaterials = includeInteriorMaterial
    ? splitMultilineValues(details.materialInterior)
    : [];
  const material = [...exteriorMaterials, ...interiorMaterials].join(", ");
  const hardware = includeHardware ? stripHtmlTags(details.culoareHW || "") : "";
  const accessories = extractAccessoryList(selectedTags);
  const location = stripHtmlTags(details.location || "");
  const designerId = stripHtmlTags(details.designerId || "");
  const manualDescriptionText = stripHtmlTags(manualDescription || "");

  const footwearSizeNote = isFootwearCategory
    ? sanitizeFootwearObservation(details.marimeDimensiune || "")
    : "";
  let sizeText = "";
  if (isFootwearCategory) {
    sizeText = [
      baseSize || lengthValue,
      heightValue && `Centimetri: ${heightValue}`,
    ]
      .filter(Boolean)
      .join(" / ");
    if (footwearSizeNote) {
      sizeText = [sizeText, `Observații mărime: ${footwearSizeNote}`]
        .filter(Boolean)
        .join(" / ");
    }
  } else if (isAccessoryHat) {
    const dimensionParts = [];
    if (baseSize) dimensionParts.push(`Mărime: ${baseSize}`);
    if (lengthValue) dimensionParts.push(`Circumferință: ${lengthValue}`);
    if (heightValue)
      dimensionParts.push(`Înălțime: ${heightValue}`);
    sizeText = dimensionParts.filter(Boolean).join(" / ");
  } else if (isApparelCategory) {
    const apparelParts = [];
    if (baseSize) apparelParts.push(`Mărime: ${baseSize}`);
    const bustValue = formatDimensionValue(details.bustDimensiune || "");
    const sleeveValue = formatDimensionValue(details.sleeveDimensiune || "");
    const shoulderValue = formatDimensionValue(details.shoulderDimensiune || "");
    if (bustValue) apparelParts.push(`Bust: ${bustValue}`);
    if (sleeveValue) apparelParts.push(`Mânecă: ${sleeveValue}`);
    if (shoulderValue) apparelParts.push(`Umăr: ${shoulderValue}`);
    if (lengthValue) apparelParts.push(`Lungime: ${lengthValue}`);
    sizeText = apparelParts.filter(Boolean).join(" / ");
  } else {
    const dimensionParts = [];
    const primaryLabel = isAccessoryHat ? "Circumferință" : "Lungime";
    const heightLabel = isAccessoryHat
      ? "Înălțime"
      : isAccessoryScarf || isBagCategory
        ? "Lățime"
        : "Înălțime";
    if (isBagCategory) {
      if (heightValue) dimensionParts.push(`${heightLabel}: ${heightValue}`);
      if (lengthValue) dimensionParts.push(`Lungime: ${lengthValue}`);
    } else {
      if (lengthValue) dimensionParts.push(`${primaryLabel}: ${lengthValue}`);
      if (heightValue) dimensionParts.push(`${heightLabel}: ${heightValue}`);
    }
    if (depthValue) dimensionParts.push(`Adâncime: ${depthValue}`);
    sizeText = [baseSize, ...dimensionParts].filter(Boolean).join(" / ");
  }

  const extraDetailsParts = [];
  if (details.culoareExterioara)
    extraDetailsParts.push(
      `Culoare exterioară: ${stripHtmlTags(details.culoareExterioara)}`
    );
  if (includeInteriorColor && details.culoareInterioara)
    extraDetailsParts.push(
      `Culoare interioară: ${stripHtmlTags(details.culoareInterioara)}`
    );
  if (exteriorMaterials.length)
    extraDetailsParts.push(
      `Material exterior: ${exteriorMaterials.join(", ")}`
    );
  if (interiorMaterials.length)
    extraDetailsParts.push(
      `Material interior: ${interiorMaterials.join(", ")}`
    );
  extraDetailsParts.push("Stare: Pre-owned");
  if (location) extraDetailsParts.push(`Locație: ${location}`);
  if ((isAccessoryHat || isApparelCategory) && baseSize)
    extraDetailsParts.push(`Mărime: ${baseSize}`);
  if (isApparelCategory) {
    const bustValue = formatDimensionValue(details.bustDimensiune || "");
    const sleeveValue = formatDimensionValue(details.sleeveDimensiune || "");
    const shoulderValue = formatDimensionValue(details.shoulderDimensiune || "");
    if (bustValue) extraDetailsParts.push(`Bust: ${bustValue}`);
    if (sleeveValue) extraDetailsParts.push(`Mânecă: ${sleeveValue}`);
    if (shoulderValue) extraDetailsParts.push(`Umăr: ${shoulderValue}`);
    if (lengthValue) extraDetailsParts.push(`Lungime: ${lengthValue}`);
  }
  if (designerId) extraDetailsParts.push(`Designer ID: ${designerId}`);
  if (manualDescriptionText)
    extraDetailsParts.push(`Caracteristici: ${manualDescriptionText}`);
  const dimensionSummaryValues = [];
  if (isAccessoryHat || isApparelCategory) {
    if (baseSize) dimensionSummaryValues.push(`Mărime: ${baseSize}`);
    if (isAccessoryHat && lengthValue)
      dimensionSummaryValues.push(`Circumferință: ${lengthValue}`);
    if (heightValue && isAccessoryHat)
      dimensionSummaryValues.push(`Înălțime: ${heightValue}`);
    if (isApparelCategory) {
      const bustValue = formatDimensionValue(details.bustDimensiune || "");
      const sleeveValue = formatDimensionValue(details.sleeveDimensiune || "");
      const shoulderValue = formatDimensionValue(details.shoulderDimensiune || "");
      if (bustValue) dimensionSummaryValues.push(`Bust: ${bustValue}`);
      if (sleeveValue) dimensionSummaryValues.push(`Mânecă: ${sleeveValue}`);
      if (shoulderValue) dimensionSummaryValues.push(`Umăr: ${shoulderValue}`);
      if (lengthValue) dimensionSummaryValues.push(`Lungime: ${lengthValue}`);
    } else if (heightValue) {
      dimensionSummaryValues.push(`Înălțime: ${heightValue}`);
    }
  } else if (isBagCategory) {
    if (heightValue) dimensionSummaryValues.push(heightValue);
    if (lengthValue) dimensionSummaryValues.push(lengthValue);
  } else {
    if (lengthValue) dimensionSummaryValues.push(lengthValue);
    if (heightValue) dimensionSummaryValues.push(heightValue);
  }
  if (depthValue) dimensionSummaryValues.push(depthValue);
  const dimensionSummary = dimensionSummaryValues.filter(Boolean).join(" | ");
  if (dimensionSummary) {
    extraDetailsParts.push(`Dimensiuni: ${dimensionSummary}`);
  }

  return {
    brand,
    model: model || details.subcategorie || "",
    category: details.categorieProd || "Accesorii",
    size: sizeText,
    color,
    material,
    hardware,
    year: "",
    condition: stripHtmlTags(condition || ""),
    accessories,
    language: "ro",
    extraDetails: extraDetailsParts.join(" || "),
  };
};

const extractBrandModelFromSummary = (summary = "") => {
  if (!summary) {
    return { brand: "", model: "" };
  }
  const trimmed = summary.trim();
  if (!trimmed) {
    return { brand: "", model: "" };
  }
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  const working = [...tokens];
  while (
    working.length &&
    /^[\d\-\#]+$/.test(working[0].replace(/[^\d\-\#]/g, ""))
  ) {
    working.shift();
  }
  if (!working.length) {
    return { brand: "", model: "" };
  }
  const normalizedTokens = working.map((token) =>
    normalizeBrandToken(token)
  );
  let matchedBrandOutput = "";
  let tokensUsed = 0;
  for (const dictionaryEntry of BRAND_DICTIONARY) {
    const normalizedBrand = normalizeBrandToken(dictionaryEntry);
    if (!normalizedBrand) continue;
    const brandParts = normalizedBrand.split(" ").filter(Boolean);
    if (!brandParts.length) continue;
    const candidate = normalizedTokens
      .slice(0, brandParts.length)
      .join(" ")
      .trim();
    if (candidate === normalizedBrand) {
      matchedBrandOutput = dictionaryEntry.toUpperCase();
      tokensUsed = brandParts.length;
      break;
    }
  }
  if (!matchedBrandOutput) {
    const fallback = normalizedTokens[0] || "";
    matchedBrandOutput = fallback;
    tokensUsed = fallback ? 1 : 0;
  }
  const model = working.slice(tokensUsed).join(" ").trim();
  return { brand: matchedBrandOutput, model };
};

const buildCategoryHierarchy = (categories) => {
  const categoryMap = {};
  const roots = [];
  categories.forEach((category) => {
    categoryMap[category.id] = { ...category, children: [] };
  });
  categories.forEach((category) => {
    if (category.parent === 0) {
      roots.push(categoryMap[category.id]);
    } else {
      const parentNode = categoryMap[category.parent];
      if (parentNode) {
        parentNode.children.push(categoryMap[category.id]);
      }
    }
  });
  return roots;
};

const WOO_CATEGORIES_ENDPOINT = `${BASE_URL}/api/tasks/woo/categories?per_page=100`;

const renderCategoryOptions = (categories, level = 0, ancestors = []) => {
  if (!Array.isArray(categories) || !categories.length) return [];
  return categories.flatMap((category) => [
    {
      ...category,
      level,
      ancestors,
      label: `${"—".repeat(level)} ${category.name}`,
      value: category.id,
      key: `${category.id}-${level}-${category.name}`,
    },
    ...renderCategoryOptions(category.children || [], level + 1, [
      ...ancestors,
      category.id,
    ]),
  ]);
};

const glassInputStyles = {
  "& .MuiInputBase-root": {
    background: "rgba(15,23,42,0.45)",
    color: "#F8FAFC",
    borderRadius: 3,
  },
  "& .MuiInputLabel-root": {
    color: "rgba(248,250,252,0.75)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(148,163,184,0.4)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(148,163,184,0.6)",
  },
};

const SectionCard = ({ title, subtitle, icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 5,
      border: "1px solid rgba(255,255,255,0.1)",
      background:
        "linear-gradient(140deg, rgba(15,23,42,0.76), rgba(30,64,175,0.65))",
      boxShadow: "0 35px 70px rgba(15,23,42,0.55)",
      color: "white",
      backdropFilter: "blur(15px)",
    }}
  >
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)", pb: 1, mb: 3 }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          background: "rgba(59,130,246,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          fontWeight: 700,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {children}
  </Paper>
);

const rotatePulse = keyframes`
  0% { transform: rotate(0deg) scale(1); box-shadow: 0 0 0 rgba(248,250,252,0.35); }
  50% { transform: rotate(180deg) scale(1.1); box-shadow: 0 0 25px rgba(248,250,252,0.35); }
  100% { transform: rotate(360deg) scale(1); box-shadow: 0 0 0 rgba(248,250,252,0.35); }
`;

const FisaProdusCard = ({
  fisaProdusStatus,
  condition,
  onChangeCondition,
  description,
  onChangeDescription,
  salePrice,
  onChangeSalePrice,
  onRephrase,
  availableTags,
  selectedTags,
  onChangeTags,
  availableBadges,
  selectedBadges,
  onToggleBadge,
  productDetails,
  onChangeProductDetail,
  productDetailOptions,
  onSave,
  onStatusCheck,
  onStatusCiorna,
  syncToPlatform,
  onToggleSyncDestination,
  issueId,
  summary,
  sku,
  onRequestWooRefresh,
  isWooLoading = false,
  isInitialFisaLoading = false,
  rephraseError = "",
  wooSyncStatus,
  onClearWooSyncStatus,
}) => {
  const details = productDetails || {};
  const options = productDetailOptions || {};
  const [isRephraseAnimating, setIsRephraseAnimating] = useState(false);
  const [showInitialLoading, setShowInitialLoading] = useState(false);
  const initialLoadingTimerRef = React.useRef(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuccess, setAiSuccess] = useState(null);
  const [autoAiTriggered, setAutoAiTriggered] = useState(false);
  const [isGeneratingWooDescription, setIsGeneratingWooDescription] =
    useState(false);
  const [wooDescriptionError, setWooDescriptionError] = useState(null);
  const [wooDescriptionMessage, setWooDescriptionMessage] = useState(null);
  const [wooDescriptionTab, setWooDescriptionTab] = useState("visual");
  const [wooCategoryOptions, setWooCategoryOptions] = useState([]);
  const [loadingWooCategories, setLoadingWooCategories] = useState(false);
  const [materialRows, setMaterialRows] = useState({
    materialExterior: 1,
    materialInterior: 1,
  });
  const [wooFetchStatus, setWooFetchStatus] = useState("idle");
  const [wooFetchError, setWooFetchError] = useState(null);
  const [isWooDetailsLoadingLocal, setIsWooDetailsLoadingLocal] =
    useState(false);
  const wooProductRef = React.useRef(null);
  const lastWooSkuRef = React.useRef("");
  const effectiveWooLoading = isWooLoading || isWooDetailsLoadingLocal;

  React.useEffect(() => {
    if (isInitialFisaLoading) {
      setShowInitialLoading(true);
      if (initialLoadingTimerRef.current) {
        clearTimeout(initialLoadingTimerRef.current);
      }
      initialLoadingTimerRef.current = setTimeout(() => {
        setShowInitialLoading(false);
        initialLoadingTimerRef.current = null;
      }, 5000);
    } else {
      setShowInitialLoading(false);
      if (initialLoadingTimerRef.current) {
        clearTimeout(initialLoadingTimerRef.current);
        initialLoadingTimerRef.current = null;
      }
    }
    return () => {
      if (initialLoadingTimerRef.current) {
        clearTimeout(initialLoadingTimerRef.current);
        initialLoadingTimerRef.current = null;
      }
    };
  }, [isInitialFisaLoading]);

  const applyWooProductDetailsFromWoo = React.useCallback(
    (wooProduct) => {
      if (!wooProduct) return { missingKeys: ["product"] };
      const stripHtml = (value = "") => {
        if (!value && value !== 0) return "";
        return String(value || "")
          .replace(/<br\s*\/?>/gi, "||")
          .replace(/\r?\n+/g, "||")
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      };
      const stripLabelPrefixes = (text = "", prefixes = []) => {
        if (!text) return "";
        const trimmed = text.trim();
        const lower = trimmed.toLowerCase();
        for (const prefix of prefixes) {
          const lowerPrefix = prefix.toLowerCase();
          if (lower.startsWith(lowerPrefix)) {
            return trimmed
              .slice(prefix.length)
              .replace(/^[:\s-]+/, "")
              .trim();
          }
        }
        return trimmed;
      };
      const sanitizeObservationValue = (value = "") =>
        stripLabelPrefixes(stripHtml(value), [
          "Observații mărime",
          "Observatii marime",
          "Observații marimi",
          "Observatii marimi",
        ]);
      const sanitizeSizeValue = (value = "") =>
        stripLabelPrefixes(stripHtml(value), [
          "Mărime",
          "Marime",
          "Size",
          "Mărime încălțăminte",
          "Marime incaltaminte",
        ])
          .replace(/\s*cm$/i, "")
          .trim();
      const attrValue = (name) => {
        const options = getWooAttributeOptions(wooProduct, name);
        return Array.isArray(options) && options.length ? options[0] : "";
      };
      const attrValueAny = (names = []) => {
        for (const name of names) {
          const value = attrValue(name);
          if (value) return value;
        }
        return "";
      };
      const attrValueLoose = (tokens = []) => {
        const normalizedTokens = tokens
          .map((token) => normalizeKey(token))
          .filter(Boolean);
        if (!normalizedTokens.length) return "";
        const attributes = Array.isArray(wooProduct?.attributes)
          ? wooProduct.attributes
          : [];
        for (const attr of attributes) {
          const attrName = normalizeKey(attr?.name || "");
          const attrSlug = normalizeKey(attr?.slug || "");
          const matches = normalizedTokens.some(
            (token) => attrName.includes(token) || attrSlug.includes(token),
          );
          if (matches) {
            const options = Array.isArray(attr?.options) ? attr.options : [];
            const first = options.find((opt) => String(opt || "").trim());
            if (first) {
              return first;
            }
          }
        }
        return "";
      };
      const attrOptions = (name) => {
        const options = getWooAttributeOptions(wooProduct, name);
        return Array.isArray(options) ? [...options] : [];
      };
      const detailMap = buildWooDetailMap(wooProduct.description || "");
      const shortDetailMap = buildWooDetailMap(
        wooProduct.short_description || "",
      );
      const mergedDetailMap = { ...shortDetailMap, ...detailMap };
      const normalizeKey = (value = "") =>
        String(value || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "")
          .trim();
      const detailMapNormalized = Object.entries(mergedDetailMap).reduce(
        (acc, [key, value]) => {
          const normalized = normalizeKey(key);
          if (normalized && !acc[normalized]) {
            acc[normalized] = value;
          }
          return acc;
        },
        {},
      );
      const detailValue = (...labels) => {
        for (const key of labels) {
          if (key && mergedDetailMap[key]) {
            return mergedDetailMap[key];
          }
        }
        return "";
      };
      const detailValueLoose = (...labels) => {
        for (const key of labels) {
          const normalized = normalizeKey(key);
          if (normalized && detailMapNormalized[normalized]) {
            return detailMapNormalized[normalized];
          }
        }
        return "";
      };
      const extractLabeledValueFromHtml = (html = "", labels = []) => {
        if (!html) return "";
        const normalizedLabels = labels
          .map((label) => normalizeKey(label))
          .filter(Boolean);
        if (!normalizedLabels.length) return "";
        const text = String(html || "")
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<\/p>/gi, "\n")
          .replace(/<[^>]+>/g, " ")
          .replace(/\u00a0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const lines = text
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);
        for (const line of lines) {
          const normalizedLine = normalizeKey(line);
          for (const label of normalizedLabels) {
            if (normalizedLine.startsWith(label)) {
              const raw = line.slice(label.length).replace(/^[:\s-]+/, "");
              return raw.trim();
            }
          }
        }
        return "";
      };
      const normalizeListValue = (value) => {
        if (!value) {
          return "";
        }
        const base = String(value || "")
          .replace(/\|\|/g, "\n")
          .replace(/\r/g, "\n");
        const tokens = base
          .split(/[\n,;]+/)
          .map((token) => token.replace(/\s+/g, " ").trim())
          .filter(Boolean);
        return tokens.length ? tokens.join("\n") : base.trim();
      };
      const pickFirstListValue = (value) => {
        const normalized = normalizeListValue(value);
        if (!normalized) return "";
        const [first] = normalized.split("\n");
        return (first || "").trim();
      };
      const metaData = wooProduct.meta_data || [];
      const findMetaValue = (keys = []) => {
        const normalizedKeys = keys.map((key) => normalizeKey(key));
        const match = metaData.find((meta) =>
          normalizedKeys.includes(normalizeKey(meta?.key || "")),
        );
        return match?.value ?? "";
      };
      const findMetaValueLoose = (tokens = []) => {
        const normalizedTokens = tokens
          .map((token) => normalizeKey(token))
          .filter(Boolean);
        if (!normalizedTokens.length) return "";
        const match = metaData.find((meta) => {
          const metaKey = normalizeKey(meta?.key || "");
          return normalizedTokens.some((token) => metaKey.includes(token));
        });
        return match?.value ?? "";
      };
      const layawayState = deriveLayawayStateFromMeta(metaData);
      const metaLocationEntry = metaData.find(
        (meta) =>
          (meta?.key || "").toLowerCase() ===
          WOO_LOCATION_META_KEY.toLowerCase(),
      );
      const locationFromMeta = metaLocationEntry?.value || "";
      const locationFromCategories = (wooProduct.categories || []).some((cat) =>
        (cat?.name || "").toLowerCase().includes("boutique"),
      )
        ? "Luxura Boutique"
        : "";
      const msrpFromMeta =
        findMetaValue([
          "msrp_price",
          "_msrp_price",
          "msrp",
          "retail_price",
          "pret_retail",
          "pret_retail_ron",
        ]) || findMetaValueLoose(["msrp", "retail", "pretretail"]);
      const msrpFromProductField =
        wooProduct?.msrp_price ||
        wooProduct?.df_msrp_price ||
        wooProduct?.msrp ||
        "";
      const attributeCategories = attrOptions("categorie");
      const attributeSubcategories = attrOptions("subcategorie");
      const wooCategoriesRaw = Array.isArray(wooProduct.categories)
        ? wooProduct.categories
        : [];
      const normalizeCategoryToken = (value = "") =>
        normalizeSimple(value).replace(/[^a-z0-9]+/g, "");
      const normalizedWooCategoryNames = wooCategoriesRaw
        .map((cat) => ({
          normalized: normalizeSimple(cat?.name || ""),
          compact: normalizeCategoryToken(cat?.name || ""),
        }))
        .filter((entry) => entry.normalized);
      const designerBagMarkersRaw = [
        "genti de designer pre-owned",
        "genti de designeri pre-owned",
      ];
      const designerBagMarkers = designerBagMarkersRaw.map((marker) =>
        normalizeSimple(marker),
      );
      const designerBagCompactMarkers = designerBagMarkersRaw.map((marker) =>
        normalizeCategoryToken(marker),
      );
      const hasDesignerBagCategory = normalizedWooCategoryNames.some(
        ({ normalized, compact }) =>
          designerBagMarkers.some((marker) => normalized.includes(marker)) ||
          designerBagCompactMarkers.some((marker) => compact.includes(marker)),
      );

      const updates = {};
      const maybeAssign = (key, value) => {
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
        ) {
          updates[key] = value;
        }
      };
      updates.existingCategoryAttributes = attributeCategories;
      updates.existingSubcategoryAttributes = attributeSubcategories;

      let categorieValue =
        detailValue("Categorie", "Categorie Prod") ||
        attrValue("categorie") ||
        wooProduct.categories?.[0]?.name ||
        "";
      if (hasDesignerBagCategory) {
        categorieValue = "Genti";
      }
      maybeAssign("categorieProd", categorieValue);
      const normalizedCategorieValue = normalizeSimple(categorieValue || "");
      const isFootwearCategory =
        normalizedCategorieValue.includes("incaltaminte");

      maybeAssign(
        "subcategorie",
        detailValue("Subcategorie") || attrValue("subcategorie"),
      );
      maybeAssign("pentru", detailValue("Pentru") || attrValue("pentru"));
      const primarySizeValueRaw = detailValue("Mărime", "Marime");
      const observationsSizeValueRaw = detailValue(
        "Observații mărime",
        "Observatii marime",
      );
      const dimensionRawSource =
        primarySizeValueRaw ||
        observationsSizeValueRaw ||
        wooProduct.description ||
        wooProduct.short_description ||
        "";
      const parsedSegments = parseDimensionSegments(
        stripHtml(dimensionRawSource),
      );
      const observationFromSegments =
        parsedSegments.sizeObservation ||
        parsedSegments.extra?.observatiimarime ||
        parsedSegments.extra?.observatiimarimi ||
        "";
      const normalizedObservationSize = sanitizeObservationValue(
        observationsSizeValueRaw || observationFromSegments,
      );
      const resolvedSizeValueRaw =
        parsedSegments.size || pickFirstListValue(primarySizeValueRaw);
      const normalizedResolvedSize = sanitizeSizeValue(
        resolvedSizeValueRaw || primarySizeValueRaw,
      );
      if (normalizedObservationSize) {
        maybeAssign("marimeDimensiune", normalizedObservationSize);
      }
      const bustValue = detailValue("Bust") || parsedSegments.bust;
      const sleeveValue =
        detailValue("Mânecă", "Maneca") || parsedSegments.sleeve;
      const shoulderValue =
        detailValue("Umăr", "Umar") || parsedSegments.shoulder;
      if (bustValue) {
        maybeAssign("bustDimensiune", bustValue);
      }
      if (sleeveValue) {
        maybeAssign("sleeveDimensiune", sleeveValue);
      }
      if (shoulderValue) {
        maybeAssign("shoulderDimensiune", shoulderValue);
      }

      const colorExt = detailValue("Culoare Exterioară", "Culoare") || "";
      maybeAssign("culoareExterioara", colorExt);
      maybeAssign(
        "culoareInterioara",
        detailValue("Culoare Interioară", "Culoare interior"),
      );

      const materialExt = normalizeListValue(
        detailValue("Material Exterior", "Material"),
      );
      const materialInt = normalizeListValue(detailValue("Material Interior"));
      maybeAssign("materialExterior", materialExt);
      maybeAssign("materialInterior", materialInt);

      const hardwareColor =
        detailValueLoose(
          "Culoare Hardware",
          "Culoare HW",
          "Hardware",
          "Hardware Color",
          "Culoare metal",
        ) ||
        attrValueAny([
          "culoare hardware",
          "culoare hw",
          "hardware",
          "hardware color",
          "culoare metal",
        ]);
      maybeAssign("culoareHW", hardwareColor);
      maybeAssign(
        "designerId",
        detailValue("Designer ID") || attrValue("designerid"),
      );
      maybeAssign(
        "entrupyId",
        detailValue("Entrupy ID") || attrValue("entrupy"),
      );

      const resolvedLocation =
        detailValue("Locație") || locationFromMeta || locationFromCategories;
      maybeAssign("location", resolvedLocation);

      const dims = wooProduct.dimensions || {};
      const panjang =
        detailValue("Lungime") ||
        parsedSegments.length ||
        parsedSegments.width ||
        parsedSegments.circumference;
      const inaltimeVal =
        detailValue("Înălțime", "Inaltime", "Circumferință") ||
        parsedSegments.height ||
        parsedSegments.circumference;
      const adancimeVal =
        detailValue("Adâncime", "Adancime", "Lățime", "Latime") ||
        parsedSegments.depth ||
        parsedSegments.width;
      const footwearSizeValue = normalizedResolvedSize;
      if (isFootwearCategory && footwearSizeValue) {
        maybeAssign("lungime", footwearSizeValue);
      } else {
        maybeAssign("lungime", panjang || dims.length);
      }
      maybeAssign("inaltime", inaltimeVal || dims.height);
      maybeAssign("adancime", adancimeVal || dims.width);

      if (typeof layawayState.enabled === "boolean") {
        updates.layawayEnabled = layawayState.enabled;
        updates.layawayOption = layawayState.enabled ? layawayState.plan : "";
      }
      const msrpFromDetail =
        detailValueLoose(
          "Pret de retail",
          "Preț de retail",
          "Retail Price",
          "MSRP",
          "Pret retail",
        ) ||
        extractLabeledValueFromHtml(wooProduct.description || "", [
          "Pret de retail",
          "Preț de retail",
          "Retail Price",
          "MSRP",
          "Pret retail",
        ]) ||
        extractLabeledValueFromHtml(wooProduct.short_description || "", [
          "Pret de retail",
          "Preț de retail",
          "Retail Price",
          "MSRP",
          "Pret retail",
        ]) ||
        attrValueAny([
          "msrp",
          "pret retail",
          "pret de retail",
          "retail price",
        ]) ||
        attrValueLoose([
          "msrp",
          "pret retail",
          "pret de retail",
          "retail price",
        ]);
      maybeAssign(
        "msrpPrice",
        msrpFromDetail || msrpFromMeta || msrpFromProductField,
      );
      maybeAssign(
        "wooDescription",
        extractDescriereBlock(wooProduct.description || ""),
      );
      const wooCategoriesSelected = Array.isArray(wooProduct.categories)
        ? wooProduct.categories
            .filter(
              (cat) => (cat?.name || "").toLowerCase() !== "fara categorie",
            )
            .map((cat) => ({
              id: cat.id,
              name: cat.name,
              label: cat.name,
              value: cat.id,
              parent: cat.parent ?? null,
              ancestors: [],
            }))
        : [];
      if (wooCategoriesSelected.length) {
        updates.wooCategoriesSelected = wooCategoriesSelected;
      }

      Object.entries(updates).forEach(([key, value]) => {
        onChangeProductDetail(key, value);
      });

      const resolvedWooPriceRaw =
        wooProduct.price ||
        wooProduct.regular_price ||
        wooProduct.sale_price ||
        findMetaValue([
          "_price",
          "_regular_price",
          "_sale_price",
          "price",
          "regular_price",
          "sale_price",
        ]) ||
        "";
      const resolvedWooPrice = String(resolvedWooPriceRaw || "").trim();
      if (resolvedWooPrice !== "" && typeof onChangeSalePrice === "function") {
        onChangeSalePrice(resolvedWooPrice);
      }

      const requiredKeys = [
        "categorieProd",
        "subcategorie",
        "materialExterior",
        "materialInterior",
        "culoareExterioara",
        "lungime",
        "inaltime",
        "adancime",
        "msrpPrice",
        "wooDescription",
      ];
      const resolvedDetails = { ...details, ...updates };
      const missingKeys = requiredKeys.filter((key) => {
        const value = resolvedDetails[key];
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return value === undefined || value === null || String(value).trim() === "";
      });
      return { missingKeys };
    },
    [details, onChangeProductDetail, onChangeSalePrice],
  );

  const reapplyWooSnapshot = React.useCallback(() => {
    if (wooProductRef.current) {
      const { missingKeys } = applyWooProductDetailsFromWoo(
        wooProductRef.current,
      );
      setWooFetchStatus(missingKeys.length ? "error" : "success");
      setWooFetchError(
        missingKeys.length
          ? `Lipsesc campuri: ${missingKeys.join(", ")}`
          : null,
      );
    }
  }, [applyWooProductDetailsFromWoo]);

  const handleWooRefresh = React.useCallback(
    async (forceFetch = true) => {
      const trimmedSku = String(sku || "").trim();
      if (!trimmedSku) {
        console.warn("[WooFetch] SKU lipsa sau gol.", {
          skuProp: sku,
          detailsSku: details.sku,
          issueId,
          summary,
        });
        setWooFetchStatus("error");
        setWooFetchError("SKU lipsa.");
        return;
      }
      if (!forceFetch && wooProductRef.current) {
        reapplyWooSnapshot();
        return;
      }
      setWooFetchStatus("loading");
      setWooFetchError(null);
      setIsWooDetailsLoadingLocal(true);
      try {
        const url = `${BASE_URL}/api/tasks/woo/product`;
        console.log("[WooFetch] Request start", {
          url,
          sku: trimmedSku,
          issueId,
          summary,
        });
        const response = await axios.get(url, {
          params: { sku: trimmedSku },
        });
        console.log("[WooFetch] Response", {
          status: response?.status,
          hasProduct: Boolean(response.data),
        });
        let wooProduct = response.data || null;
        if (!wooProduct) {
          const numericIdRaw = trimmedSku.replace(/\D/g, "");
          const numericId = numericIdRaw ? parseInt(numericIdRaw, 10) : null;
          console.log("[WooFetch] Fallback by ID", {
            numericId,
            sku: trimmedSku,
          });
          if (numericId) {
            try {
              const byId = await axios.get(url, { params: { id: numericId } });
              wooProduct = byId.data || null;
            } catch (idError) {
              console.warn("[WooFetch] fallback by id failed", idError);
            }
          }
        }
        if (!wooProduct) {
          console.warn("[WooFetch] Produs inexistent in Woo", {
            sku: trimmedSku,
          });
          setWooFetchStatus("error");
          setWooFetchError("Nu am gasit produsul in WooCommerce.");
          return;
        }
        wooProductRef.current = wooProduct;
        lastWooSkuRef.current = trimmedSku;
        const { missingKeys } = applyWooProductDetailsFromWoo(wooProduct);
        if (missingKeys.length) {
          console.warn("[WooFetch] Campuri lipsa dupa mapare", {
            sku: trimmedSku,
            missingKeys,
          });
          setWooFetchStatus("error");
          setWooFetchError(`Lipsesc campuri: ${missingKeys.join(", ")}`);
        } else {
          setWooFetchStatus("success");
        }
      } catch (error) {
        console.error("[WooFetch] error:", error);
        setWooFetchStatus("error");
        setWooFetchError("Eroare la citirea din WooCommerce.");
      } finally {
        setIsWooDetailsLoadingLocal(false);
      }
    },
    [applyWooProductDetailsFromWoo, reapplyWooSnapshot, sku],
  );

  React.useEffect(() => {
    const trimmedSku = String(sku || "").trim();
    if (!trimmedSku) {
      console.warn("[WooFetch] SKU lipsa la init", {
        skuProp: sku,
        detailsSku: details.sku,
        issueId,
      });
      setWooFetchStatus("error");
      setWooFetchError("SKU lipsa.");
      return;
    }
    if (lastWooSkuRef.current !== trimmedSku) {
      console.log("[WooFetch] SKU changed, refetch", {
        from: lastWooSkuRef.current,
        to: trimmedSku,
      });
      handleWooRefresh(true);
    }
  }, [handleWooRefresh, sku]);

  const normalizeOptions = (list, fallback = []) => {
    const src = Array.isArray(list) && list.length ? list : fallback;
    return src.map((item) => {
      if (typeof item === "string") {
        return { key: item, label: item, value: item };
      }
      const label =
        item.label ||
        item.name ||
        item.value ||
        item.id ||
        "";
      const value =
        item.value !== undefined &&
        item.value !== null &&
        String(item.value).trim() !== ""
          ? item.value
          : item.id || label;
      return {
        key: item.id || value || label,
        label,
        value,
      };
    });
  };
  const sanitizeWooCategoryList = (list = []) =>
    (Array.isArray(list) ? list : [])
      .map((cat) => {
        if (!cat) return null;
        const rawId =
          cat?.id !== undefined && cat?.id !== null
            ? cat.id
            : cat?.value !== undefined && cat?.value !== null
              ? cat.value
              : cat;
        const numericId = Number(rawId);
        if (!Number.isInteger(numericId) || numericId <= 0) {
          return null;
        }
        const optionMatch = wooCategoryOptions.find(
          (opt) => opt?.id === numericId
        );
        const ancestorsSource = Array.isArray(cat?.ancestors)
          ? cat.ancestors
          : optionMatch?.ancestors || [];
        const normalizedAncestors = ancestorsSource
          .map((ancestorId) => Number(ancestorId))
          .filter((ancestorId) => Number.isInteger(ancestorId) && ancestorId > 0);
        const parentCandidate =
          cat?.parent !== undefined && cat?.parent !== null
            ? Number(cat.parent)
            : normalizedAncestors.length
              ? normalizedAncestors[normalizedAncestors.length - 1]
              : optionMatch?.parent ?? null;
        const parent =
          Number.isInteger(parentCandidate) && parentCandidate > 0
            ? parentCandidate
            : null;
        const name = cat?.name || cat?.label || optionMatch?.name || "";
        const level =
          typeof cat?.level === "number"
            ? cat.level
            : typeof optionMatch?.level === "number"
              ? optionMatch.level
              : 0;
        const label =
          cat?.label || (level > 0 ? `${"—".repeat(level)} ${name}` : name);
        return {
          id: numericId,
          value: numericId,
          name,
          label: label || name,
          parent,
          ancestors: normalizedAncestors,
        };
      })
      .filter(
        (cat) =>
          cat &&
          (cat.name || "").toLowerCase() !== "fara categorie" &&
          Number.isInteger(cat.id) &&
          cat.id > 0
      );
  const wooCategoriesValue = sanitizeWooCategoryList(
    details.wooCategoriesSelected || []
  );

  const categorieOptions = normalizeOptions(options.categorieProd || []);
  const categoryDisplayLabel = React.useMemo(() => {
    if (!details.categorieProd) {
      return "";
    }
    const match = categorieOptions.find(
      (option) =>
        option.value === details.categorieProd ||
        option.key === details.categorieProd ||
        option.label === details.categorieProd
    );
    return match?.label || details.categorieProd || "";
  }, [details.categorieProd, categorieOptions]);
  const normalizedCategory = normalizeText(categoryDisplayLabel);
  const normalizedSubcategory = normalizeText(details.subcategorie || "");
  const categoryMatches = (needle) => {
    const normalizedNeedle = normalizeText(needle);
    if (!normalizedNeedle) {
      return false;
    }
    return (
      normalizedCategory === normalizedNeedle ||
      normalizedCategory.includes(normalizedNeedle)
    );
  };
  const isFootwearCategory = categoryMatches("incaltaminte");
  const isAccessoryScarf =
    categoryMatches("accesorii") && normalizedSubcategory.includes("esarfa");
  const isApparelCategory = categoryMatches("imbracaminte");
  const isAccessoryHat =
    categoryMatches("accesorii") &&
    (normalizedSubcategory.includes("hat") ||
      normalizedSubcategory.includes("hats") ||
      normalizedSubcategory.includes("palar") ||
      normalizedSubcategory.includes("palari") ||
      normalizedSubcategory.includes("har"));
  const isAccessoryWrap =
    isAccessoryScarf || isAccessoryHat || isApparelCategory;
  const accessoryVariant = isApparelCategory
    ? "apparel"
    : isAccessoryHat
      ? "hat"
      : "scarf";
  const locationOptions = ["Exclusiv Online", "Luxura Boutique"];
  const numericSalePrice = parseLoosePriceNumber(salePrice);
  const buildLayawayOptions = () => {
    const baseOptions = ["1 Rata", "2 Rate", "3 Rate", "1 Rata Speciala"];
    if (numericSalePrice > 0 && numericSalePrice <= 2000) {
      return ["1 Rata Speciala", "1 Rata", "2 Rate", "3 Rate"];
    }
    return baseOptions;
  };
  const layawayOptions = buildLayawayOptions();
  const categoryAttributeList = Array.isArray(
    details.existingCategoryAttributes
  )
    ? details.existingCategoryAttributes.filter(Boolean)
    : [];
  const subcategoryAttributeList = Array.isArray(
    details.existingSubcategoryAttributes
  )
    ? details.existingSubcategoryAttributes.filter(Boolean)
    : [];

  const handleAttributeListChange = (fieldKey, values) => {
    if (typeof onChangeProductDetail !== "function") return;
    const normalized = (values || [])
      .map((value) => (typeof value === "string" ? value : value?.label || ""))
      .map((value) => value.trim())
      .filter(Boolean);
    onChangeProductDetail(fieldKey, normalized);
  };

  const renderAttributeChipGroup = (label, fieldKey, values) => (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={values}
        onChange={(_event, newValue) =>
          handleAttributeListChange(fieldKey, newValue || [])
        }
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={`${label}-${index}-${option}`}
              label={option}
              sx={{
                borderColor: "rgba(255,255,255,0.25)",
                color: "#E2E8F0",
                background: "rgba(15,23,42,0.35)",
              }}
              variant="outlined"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Adaugă sau șterge valori SEO"
            sx={glassInputStyles}
          />
        )}
      />
    </Box>
  );

  const renderWooAttributeAccordion = () => {
    if (!categoryAttributeList.length && !subcategoryAttributeList.length) {
      return null;
    }
    return (
      <Accordion
        sx={{
          background: "rgba(15,23,42,0.6)",
          borderRadius: "16px",
          border: "1px solid rgba(148,163,184,0.3)",
          boxShadow: "0 8px 30px rgba(15,23,42,0.35)",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#E2E8F0" }} />}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: "#E2E8F0",
              letterSpacing: 0.5,
            }}
          >
            SEO
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {renderAttributeChipGroup(
              "Categorie (atribut Woo)",
              "existingCategoryAttributes",
              categoryAttributeList
            )}
            {renderAttributeChipGroup(
              "Subcategorie (atribut Woo)",
              "existingSubcategoryAttributes",
              subcategoryAttributeList
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  React.useEffect(() => {
    if (
      !categorieOptions.length ||
      !categoryAttributeList.length ||
      details.categorieProd
    ) {
      return;
    }
    const normalizedAttributes = categoryAttributeList.map((value) =>
      value.toLowerCase()
    );
    const match = categorieOptions.find((option) =>
      normalizedAttributes.includes((option.value || "").toLowerCase())
    );
    if (match && details.categorieProd !== match.value) {
      onChangeProductDetail("categorieProd", match.value);
      reapplyWooSnapshot();
    }
  }, [
    categorieOptions,
    categoryAttributeList,
    details.categorieProd,
    onChangeProductDetail,
    reapplyWooSnapshot,
  ]);

  React.useEffect(() => {
    if (!categorieOptions.length || !wooCategoriesValue.length) {
      return;
    }
    const normalizeForMatch = (value) =>
      normalizeText(value)
        .replace(/[^a-z0-9]+/g, "")
        .trim();
    const designerBagMarkers = [
      "gentidesignerpreowned",
      "gentidedesigneripreowned",
    ];
    const normalizedWooNames = wooCategoriesValue
      .map((cat) =>
        normalizeForMatch(cat?.name ?? cat?.label ?? cat?.value ?? "")
      )
      .filter(Boolean);
    if (!normalizedWooNames.length) {
      return;
    }

    const normalizeRawValue = (raw) =>
      raw === undefined || raw === null ? "" : String(raw).trim();
    const existingCategoryValue = normalizeRawValue(details.categorieProd);
    const hasValidExistingSelection =
      existingCategoryValue &&
      categorieOptions.some(
        (option) => normalizeRawValue(option.value) === existingCategoryValue
      );
    const normalizedExistingCategory = normalizeForMatch(existingCategoryValue);
    const hasDesignerBagsCategory = normalizedWooNames.some((name) =>
      designerBagMarkers.some((marker) => name.includes(marker))
    );
    const shouldForceDesignerCategory =
      hasDesignerBagsCategory && normalizedExistingCategory !== "genti";
    if (hasValidExistingSelection && !shouldForceDesignerCategory) {
      return;
    }

    const normalizedOptions = categorieOptions
      .map((option) => {
        const normalizedValues = [option.label, option.value, option.key]
          .map((candidate) => normalizeForMatch(candidate ?? ""))
          .filter(Boolean);
        return {
          raw: option,
          normalizedValues,
        };
      })
      .filter((entry) => entry.normalizedValues.length);

    const applyCategorySelection = (matchedOption) => {
      if (
        matchedOption &&
        matchedOption.raw &&
        details.categorieProd !== matchedOption.raw.value
      ) {
        onChangeProductDetail("categorieProd", matchedOption.raw.value);
        return true;
      }
      return false;
    };
    const optionIncludes = (option, value) =>
      option.normalizedValues.some((candidate) => candidate.includes(value));
    const optionEquals = (option, value) =>
      option.normalizedValues.includes(value);

    if (hasDesignerBagsCategory) {
      const designerBagOption =
        normalizedOptions.find((option) => optionIncludes(option, "genti")) ||
        normalizedOptions.find((option) => optionEquals(option, "genti"));
      if (applyCategorySelection(designerBagOption)) {
        return;
      }
    }

    const hasGeneralBagsCategory = normalizedWooNames.some(
      (name) => name.includes("bag") || name.includes("handbag")
    );
    if (hasGeneralBagsCategory) {
      const accessoriesOption =
        normalizedOptions.find((option) =>
          optionIncludes(option, "accesorii")
        ) ||
        normalizedOptions.find((option) => optionEquals(option, "accesorii"));
      if (applyCategorySelection(accessoriesOption)) {
        return;
      }
    }

    if (hasDesignerBagsCategory) {
      const gentiOption =
        normalizedOptions.find((option) => optionEquals(option, "genti")) ||
        normalizedOptions.find((option) => optionIncludes(option, "genti"));
      if (applyCategorySelection(gentiOption)) {
        return;
      }
    }

    const exactMatch = normalizedOptions.find((option) =>
      option.normalizedValues.some((value) =>
        normalizedWooNames.includes(value)
      )
    );
    if (applyCategorySelection(exactMatch)) {
      return;
    }

    const partialMatch = normalizedOptions.find((option) =>
      option.normalizedValues.some((value) =>
        normalizedWooNames.some(
          (name) => name.includes(value) || value.includes(name)
        )
      )
    );
    if (applyCategorySelection(partialMatch)) {
      return;
    }
  }, [
    categorieOptions,
    wooCategoriesValue,
    details.categorieProd,
    onChangeProductDetail,
    reapplyWooSnapshot,
  ]);

  const renderTextField = (fieldKey, label) => {
    const rawValue = details[fieldKey] || "";
    const shouldForceUppercase = fieldKey === "brand";
    const displayValue = shouldForceUppercase
      ? String(rawValue).toUpperCase()
      : rawValue;
    return (
      <TextField
        fullWidth
        label={label}
        value={displayValue}
        onChange={(e) => {
          const nextValue = shouldForceUppercase
            ? e.target.value.toUpperCase()
            : e.target.value;
          onChangeProductDetail(fieldKey, nextValue);
        }}
        sx={glassInputStyles}
      />
    );
  };

  const parseMultilineEntries = (value = "") => {
    if (value === null || value === undefined) {
      return [];
    }
    return String(value).replace(/\r/g, "").split("\n");
  };

  const updateMultilineEntries = (fieldKey, entries) => {
    const sanitized = entries.filter(
      (entry, index) => entry.trim() || index === entries.length - 1
    );
    onChangeProductDetail(fieldKey, sanitized.join("\n"));
  };

  const getMaterialEntries = (fieldKey) => {
    const values = parseMultilineEntries(details[fieldKey]);
    const count = Math.max(values.length || 1, materialRows[fieldKey] || 1);
    return Array.from({ length: count }, (_, index) => values[index] || "");
  };

  const renderMaterialInputs = (fieldKey, label) => {
    const entries = getMaterialEntries(fieldKey);
    return (
      <Stack spacing={1}>
        {entries.map((entry, index) => (
          <TextField
            key={`${fieldKey}-${index}`}
            fullWidth
            label={`${label}${entries.length > 1 ? ` #${index + 1}` : ""}`}
            value={entry}
            onChange={(e) => {
              const nextEntries = [...entries];
              nextEntries[index] = e.target.value;
              updateMultilineEntries(fieldKey, nextEntries);
            }}
            sx={glassInputStyles}
            InputProps={{
              endAdornment:
                index === entries.length - 1 ? (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setMaterialRows((prev) => ({
                        ...prev,
                        [fieldKey]: (prev[fieldKey] || entries.length) + 1,
                      }))
                    }
                    sx={{ color: "#93C5FD" }}
                  >
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                ) : null,
            }}
          />
        ))}
      </Stack>
    );
  };

  const renderAccessoryWrapDetails = (variant = "scarf") => {
    const isHatVariant = variant === "hat";
    const isApparelVariant = variant === "apparel";
    const specificsTitle = isHatVariant
      ? "Specificații pălărie"
      : isApparelVariant
        ? "Specificații piesă vestimentară"
        : "Specificații eșarfă";
    const firstDimensionLabel = isHatVariant
      ? "Circumferință (cm)"
      : "Lungime (cm)";
    const secondDimensionLabel = isHatVariant
      ? "Înălțime (cm)"
      : "Lățime (cm)";
    return (
      <Stack spacing={3}>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            Clasificare & Comercial
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Preț vânzare (RON)"
                type="number"
                value={salePrice || ""}
                onChange={(e) => onChangeSalePrice?.(e.target.value)}
                sx={glassInputStyles}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Preț de retail (MSRP)"
                type="number"
                value={details.msrpPrice || ""}
                onChange={(e) =>
                  onChangeProductDetail("msrpPrice", e.target.value)
                }
                sx={glassInputStyles}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Categorie Prod"
                value={details.categorieProd || ""}
                onChange={(e) =>
                  onChangeProductDetail("categorieProd", e.target.value)
                }
                sx={glassInputStyles}
              >
                <MenuItem value="">
                  <em>Necompletat</em>
                </MenuItem>
                {categorieOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextField("subcategorie", "Subcategorie")}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Pentru"
                value={details.pentru || ""}
                onChange={(e) =>
                  onChangeProductDetail("pentru", e.target.value)
                }
                sx={glassInputStyles}
              >
                <MenuItem value="">
                  <em>Necompletat</em>
                </MenuItem>
                {["Femei", "Barbati", "Copii", "Casa"].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={wooCategoryOptions}
                getOptionLabel={(option) => option?.label || option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                value={wooCategoriesValue}
                onChange={(_event, newValue) =>
                  onChangeProductDetail(
                    "wooCategoriesSelected",
                    sanitizeWooCategoryList(newValue || [])
                  )
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option?.id || index}
                      label={option?.name || option?.label}
                      {...getTagProps({ index })}
                      sx={{
                        borderColor: "rgba(255,255,255,0.4)",
                        color: "#E2E8F0",
                        background: "rgba(15,23,42,0.35)",
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Categorii Woo"
                    placeholder="Selectează categorii Woo"
                    sx={glassInputStyles}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingWooCategories ? (
                            <CircularProgress
                              color="inherit"
                              size={16}
                              sx={{ mr: 1 }}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            {specificsTitle}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderTextField("culoareExterioara", "Culoare")}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderMaterialInputs("materialExterior", "Material")}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextField("designerId", "Designer ID")}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Locație"
                value={details.location || ""}
                onChange={(e) =>
                  onChangeProductDetail("location", e.target.value)
                }
                sx={glassInputStyles}
              >
                <MenuItem value="">
                  <em>Necompletat</em>
                </MenuItem>
                {locationOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stare"
                value="Pre-owned"
                InputProps={{ readOnly: true }}
                sx={glassInputStyles}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            Dimensiuni
          </Typography>
          <Grid container spacing={2}>
            {isHatVariant ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderTextField(
                    "marimeDimensiune",
                    "Mărime (S, M, L etc.)"
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTextField("lungime", "Circumferință (cm)")}
                </Grid>
              </>
            ) : isApparelVariant ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderTextField("marimeDimensiune", "Mărime")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTextField("bustDimensiune", "Bust (cm)")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTextField("sleeveDimensiune", "Mânecă (cm)")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTextField("shoulderDimensiune", "Umăr (cm)")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTextField("lungime", "Lungime (cm)")}
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  {renderTextField("lungime", firstDimensionLabel)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderTextField("inaltime", secondDimensionLabel)}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>
    );
  };

  const renderWooCommerceSection = () => (
    <>
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          Descriere produs (Woo)
        </Typography>
        <Stack spacing={2}>
          <Box
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.14)",
              overflow: "hidden",
              background: "rgba(15,23,42,0.45)",
            }}
          >
            <Tabs
              value={wooDescriptionTab}
              onChange={(_, next) => setWooDescriptionTab(next)}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                minHeight: 44,
                p: 0.5,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, rgba(241,245,249,0.96), rgba(226,232,240,0.96))",
                border: "1px solid rgba(148,163,184,0.35)",
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: "#334155",
                  borderRadius: 2.5,
                  transition: "all .2s ease",
                },
                "& .MuiTab-root:hover": {
                  color: "#0f172a",
                  backgroundColor: "rgba(148,163,184,0.12)",
                },
                "& .Mui-selected": {
                  color: "#ffffff !important",
                  background:
                    "linear-gradient(135deg, rgba(59,130,246,0.95), rgba(99,102,241,0.95))",
                  boxShadow: "0 10px 22px rgba(59,130,246,0.35)",
                },
              }}
            >
              <Tab value="visual" label="Visual" />
              <Tab value="html" label="Woo HTML" />
            </Tabs>
          </Box>

          {wooDescriptionTab === "visual" ? (
            <Box
              sx={{
                minHeight: 220,
                maxHeight: 420,
                overflow: "auto",
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.14)",
                backgroundColor: "#fff",
                color: "#0f172a",
                p: 2,
                "& h1, & h2, & h3, & h4": {
                  margin: "12px 0 8px",
                  lineHeight: 1.35,
                },
                "& p": {
                  margin: "8px 0",
                  lineHeight: 1.6,
                },
                "& ul, & ol": {
                  margin: "8px 0 8px 20px",
                },
                "& li": {
                  margin: "4px 0",
                },
              }}
            >
              {String(details.wooDescription || "").trim() ? (
                String(details.wooDescription || "").includes("<") ? (
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: details.wooDescription || "",
                    }}
                  />
                ) : (
                  <Typography
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "inherit",
                    }}
                  >
                    {details.wooDescription || ""}
                  </Typography>
                )
              ) : (
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Nu există descriere Woo încă. Folosește tabul "Woo HTML" sau
                  butonul de generare.
                </Typography>
              )}
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={10}
              value={details.wooDescription || ""}
              onChange={handleWooDescriptionChange}
              placeholder="Introdu manual HTML-ul pentru Woo sau folosește opțiunea de generare."
              sx={glassInputStyles}
            />
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={handleGenerateWooDescription}
              disabled={isGeneratingWooDescription}
              sx={{
                background: "linear-gradient(135deg, #f97316, #fb923c)",
                color: "#111827",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 3,
                boxShadow: "0 12px 30px rgba(249,115,22,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #fb923c, #fdba74)",
                  boxShadow: "0 12px 35px rgba(251,146,60,0.4)",
                },
                "&.Mui-disabled": {
                  background: "rgba(251,146,60,0.4)",
                  color: "rgba(17,24,39,0.6)",
                },
              }}
            >
              {isGeneratingWooDescription
                ? "Se generează..."
                : "Generează descriere"}
            </Button>
          </Stack>
          {wooDescriptionError && (
            <Alert
              severity="error"
              onClose={() => setWooDescriptionError(null)}
            >
              {wooDescriptionError}
            </Alert>
          )}
          {wooDescriptionMessage && (
            <Alert
              severity="success"
              onClose={() => setWooDescriptionMessage(null)}
            >
              {wooDescriptionMessage}
            </Alert>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2,
          borderRadius: 4,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(15,23,42,0.35)",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          Layaway
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(details.layawayEnabled)}
              onChange={(e) => {
                onChangeProductDetail("layawayEnabled", e.target.checked);
                if (!e.target.checked) {
                  onChangeProductDetail("layawayOption", "");
                } else if (!details.layawayOption) {
                  const defaultPlan = layawayOptions[0];
                  if (defaultPlan) {
                    onChangeProductDetail("layawayOption", defaultPlan);
                  }
                }
              }}
            />
          }
          label="Doresc produsul prin Layaway"
        />
        {details.layawayEnabled && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Select
              value={details.layawayOption || ""}
              onChange={(e) =>
                onChangeProductDetail("layawayOption", e.target.value)
              }
              displayEmpty
              sx={glassInputStyles}
            >
              <MenuItem value="" disabled>
                Selectează planul
              </MenuItem>
              {layawayOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === "1 Rata Speciala"
                    ? "1 Rata Speciala (50% avans)"
                    : opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      {renderWooAttributeAccordion()}
    </>
  );

  const handleRephraseClick = () => {
    if (!isRephraseAnimating) {
      setIsRephraseAnimating(true);
      setTimeout(() => setIsRephraseAnimating(false), 900);
    }
    if (typeof onRephrase === "function") {
      onRephrase();
    }
  };

  const handleWooDescriptionChange = (event) => {
    onChangeProductDetail("wooDescription", event.target.value);
  };

  React.useEffect(() => {
    const fetchWooCategories = async () => {
      setLoadingWooCategories(true);
      try {
        let page = 1;
        let aggregated = [];
        while (true) {
          const response = await axios.get(
            `${WOO_CATEGORIES_ENDPOINT}&page=${page}`
          );
          const data = Array.isArray(response.data) ? response.data : [];
          aggregated = aggregated.concat(data);
          if (data.length < 100) {
            break;
          }
          page += 1;
        }
        const filtered = aggregated.filter(
          (cat) => (cat?.name || "").toLowerCase() !== "fara categorie"
        );
        const tree = buildCategoryHierarchy(filtered);
        const flatList = renderCategoryOptions(tree);
        setWooCategoryOptions(flatList);
      } catch (error) {
        console.error("Error fetching Woo categories:", error);
      } finally {
        setLoadingWooCategories(false);
      }
    };
    fetchWooCategories();
  }, []);

  React.useEffect(() => {
    setMaterialRows((prev) => ({
      ...prev,
      materialExterior: Math.max(
        parseMultilineEntries(details.materialExterior).length || 1,
        prev.materialExterior || 1
      ),
      materialInterior: Math.max(
        parseMultilineEntries(details.materialInterior).length || 1,
        prev.materialInterior || 1
      ),
    }));
  }, [details.materialExterior, details.materialInterior]);

  const handleGenerateWooDescription = async () => {
    setWooDescriptionError(null);
    setWooDescriptionMessage(null);
    setIsGeneratingWooDescription(true);
    try {
      const payload = buildWooDescriptionPayload(
        details,
        condition,
        selectedTags,
        description,
        { isAccessoryScarf, isAccessoryHat, categoryLabel: categoryDisplayLabel }
      );
      if (!payload) {
        setWooDescriptionError(
          "Nu avem suficiente date pentru a genera descrierea."
        );
        return;
      }
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/masterdata/generate-product-description",
        payload
      );
      const generatedHtml = response.data?.fullHtml;
      if (generatedHtml) {
        onChangeProductDetail("wooDescription", generatedHtml);
        setWooDescriptionMessage("Descriere generată cu succes.");
      } else {
        setWooDescriptionError("Nu am primit conținut generat.");
      }
    } catch (error) {
      console.error("Eroare la generarea descrierii Woo:", error);
      const backendError = error?.response?.data?.error;
      const backendDetails = error?.response?.data?.details;
      const detailsText =
        typeof backendDetails === "string"
          ? backendDetails
          : backendDetails
            ? JSON.stringify(backendDetails)
            : "";
      const message = [backendError, detailsText]
        .filter(Boolean)
        .join(" | ") || "Nu am putut genera descrierea produsului.";
      setWooDescriptionError(message);
    } finally {
      setIsGeneratingWooDescription(false);
    }
  };

  const renderWooStatusIcon = () => {
    if (wooFetchStatus === "loading") {
      return <CircularProgress size={18} sx={{ color: "#E0F2FE" }} />;
    }
    if (wooFetchStatus === "success") {
      return <CheckCircleIcon sx={{ color: "#86EFAC" }} />;
    }
    if (wooFetchStatus === "error") {
      return <ErrorOutlineIcon sx={{ color: "#FCA5A5" }} />;
    }
    return null;
  };

  const handleSaveClick = () => {
    console.error("[FisaProdusCard] Save button clicked", {
      hasOnSave: typeof onSave === "function",
      effectiveWooLoading,
      issueId,
      summary,
      sku,
    });
    if (effectiveWooLoading) {
      window.alert("Datele din WooCommerce sunt încă în încărcare. Încearcă din nou în câteva secunde.");
      return;
    }
    if (typeof onSave !== "function") {
      window.alert("Handlerul de salvare nu este disponibil în această instanță.");
      return;
    }
    try {
      const maybePromise = onSave();
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.catch((error) => {
          console.error("[FisaProdusCard] Save handler rejected", error);
        });
      }
    } catch (error) {
      console.error("[FisaProdusCard] Save handler threw", error);
      window.alert("A apărut o eroare la pornirea salvării. Vezi consola pentru detalii.");
    }
  };

  const renderWooFetchDebug = () => {
    if (wooFetchStatus !== "error") return null;
    return (
      <Alert
        severity="warning"
        sx={{
          background: "rgba(248, 113, 113, 0.18)",
          border: "1px solid rgba(248, 113, 113, 0.35)",
          color: "#FEE2E2",
          fontWeight: 500,
        }}
      >
        Woo fetch error: {wooFetchError || "Eroare necunoscută."}
      </Alert>
    );
  };

  const handleAiAutofill = async () => {
    if (!issueId) {
      setAiError("Nu avem issueId pentru acest produs.");
      return;
    }
    setAiError(null);
    setAiSuccess(null);
    setAiLoading(true);
    try {
      const payload = {
        issueId,
        summary: summary || details.summary || "",
        sku: sku || details.sku || "",
        brand: details.brand || "",
        model: details.model || "",
        description: description || "",
        tags: selectedTags || [],
      };
      const { data } = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/ai/product-suggestions",
        payload
      );
      const suggestions = data?.suggestions || {};
      const suggestionMap = {
        brand: "brand",
        model: "model",
        culoareExterioara: "culoareExterioara",
        culoareInterioara: "culoareInterioara",
        culoareHW: "culoareHW",
        materialExterior: "materialExterior",
        materialInterior: "materialInterior",
        marimeDimensiune: "marimeDimensiune",
        bustDimensiune: "bustDimensiune",
        sleeveDimensiune: "sleeveDimensiune",
        shoulderDimensiune: "shoulderDimensiune",
        lungime: "lungime",
        inaltime: "inaltime",
        adancime: "adancime",
        location: "location",
      };
      Object.entries(suggestionMap).forEach(([key, field]) => {
        const value = suggestions[key];
        if (value && typeof onChangeProductDetail === "function") {
          onChangeProductDetail(field, value);
        }
      });
      if (suggestions.description && typeof onChangeDescription === "function") {
        onChangeDescription(suggestions.description);
      }
      setAiSuccess("Sugestiile AI au fost aplicate. Verifică câmpurile completate.");
    } catch (error) {
      console.error("[FisaProdus] AI autofill error:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Nu am putut obține sugestiile AI.";
      setAiError(message);
    } finally {
      setAiLoading(false);
    }
  };

  React.useEffect(() => {
    setAutoAiTriggered(false);
  }, [issueId]);

  React.useEffect(() => {
    if (!summary || !onChangeProductDetail) {
      return;
    }
    const { brand: parsedBrand, model: parsedModel } =
      extractBrandModelFromSummary(summary);
    if (parsedBrand) {
      const normalizedBrand = parsedBrand.toUpperCase();
      if ((details.brand || "") !== normalizedBrand) {
        onChangeProductDetail("brand", normalizedBrand);
      }
    }
    if (parsedModel && !(details.model || "").trim()) {
      onChangeProductDetail("model", parsedModel);
    }
  }, [summary, details.brand, details.model, onChangeProductDetail]);

  React.useEffect(() => {
    if (
      syncToPlatform ||
      autoAiTriggered ||
      aiLoading ||
      !issueId ||
      !(details.brand || details.model)
    ) {
      return;
    }
    handleAiAutofill();
    setAutoAiTriggered(true);
  }, [
    autoAiTriggered,
    aiLoading,
    issueId,
    details.brand,
    details.model,
    syncToPlatform,
  ]);

  return (
    <Box sx={{ position: "relative" }}>
      <Card
        sx={{
          width: "100%",
          mb: 5,
          borderRadius: 6,
          border: "1px solid rgba(255,255,255,0.12)",
          background:
            "linear-gradient(145deg, rgba(15,23,42,0.94), rgba(7,89,133,0.82), rgba(14,165,233,0.65))",
          color: "white",
          boxShadow: "0 45px 80px rgba(15,23,42,0.55)",
          filter: effectiveWooLoading ? "blur(4px)" : "none",
          opacity: effectiveWooLoading ? 0.6 : 1,
          pointerEvents: "auto",
          transition: "filter 0.3s ease, opacity 0.3s ease",
        }}
      >
      <CardHeader
        avatar={
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "white",
              fontWeight: 700,
              fontSize: 18,
              width: 48,
              height: 48,
            }}
          >
            FP
          </Avatar>
        }
        title={
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
            Fișa Produs
          </Typography>
        }
        subheader={
          <Typography sx={{ color: "#BAE6FD", fontWeight: 500 }}>
            Status: {fisaProdusStatus || "Loading..."}
          </Typography>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {renderWooStatusIcon()}
            <IconButton
              onClick={() => handleWooRefresh(true)}
              sx={{
                color: "#E0F2FE",
                border: "1px solid rgba(255,255,255,0.25)",
                backgroundColor: "rgba(15,23,42,0.35)",
                "&:hover": { backgroundColor: "rgba(14,165,233,0.35)" },
              }}
              size="small"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        sx={{
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          pb: 2,
        }}
      />
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={4}>
          {showInitialLoading && (
            <Alert
              icon={<AutorenewIcon sx={{ color: "#BAE6FD" }} />}
              sx={{
                background: "rgba(15,118,110,0.22)",
                border: "1px solid rgba(45,212,191,0.35)",
                color: "#CCFBF1",
                fontWeight: 500,
              }}
            >
              Se sincronizează datele inițiale din JIRA și platformă.
            </Alert>
          )}
          {renderWooFetchDebug()}
          <Alert
            icon={false}
            sx={{
              background: "rgba(59,130,246,0.2)",
              border: "1px solid rgba(59,130,246,0.35)",
              color: "#E0F2FE",
              fontWeight: 500,
            }}
          >
            Statusul produsului este obținut din proiectul "PROD" pe baza
            rezumatului.
          </Alert>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              onClick={handleAiAutofill}
              disabled={aiLoading}
              sx={{
                background:
                  "linear-gradient(120deg, rgba(14,165,233,0.85), rgba(59,130,246,0.9))",
                color: "#FFFFFF",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {aiLoading ? "Analizăm..." : "Completează cu AI"}
            </Button>
          </Box>
          {aiError && (
            <Alert
              severity="error"
              onClose={() => setAiError(null)}
              sx={{ background: "rgba(239,68,68,0.12)" }}
            >
              {aiError}
            </Alert>
          )}
          {aiSuccess && (
            <Alert
              severity="success"
              onClose={() => setAiSuccess(null)}
              sx={{ background: "rgba(34,197,94,0.12)", color: "#DCFCE7" }}
            >
              {aiSuccess}
            </Alert>
          )}

          <SectionCard
            title="Condiție & descriere"
            subtitle="Completează informațiile esențiale despre produs."
            icon="I"
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Condiție Luxura"
                  value={condition}
                  onChange={(e) => onChangeCondition(e.target.value)}
                  sx={glassInputStyles}
                >
                  <MenuItem value="" disabled>
                    Selectează Condiția
                  </MenuItem>
                  {["Nou", "Excelent", "Foarte Bun", "Bun", "Rezonabil"].map(
                    (opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} mt={3} alignItems="center">
              <TextField
                label="Caracteristici specifice"
                fullWidth
                multiline
                rows={6}
                value={description}
                onChange={(e) => onChangeDescription(e.target.value)}
                sx={glassInputStyles}
              />
              <IconButton
                onClick={handleRephraseClick}
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "linear-gradient(140deg, rgba(59,130,246,0.4), rgba(14,165,233,0.7))",
                  color: "#F8FAFC",
                  animation: isRephraseAnimating ? `${rotatePulse} 0.9s ease` : "none",
                }}
              >
                <AutorenewIcon />
              </IconButton>
            </Stack>
            {rephraseError ? (
              <Alert
                severity="error"
                sx={{
                  mt: 2,
                  background: "rgba(127,29,29,0.22)",
                  border: "1px solid rgba(248,113,113,0.35)",
                  color: "#FECACA",
                }}
              >
                {rephraseError}
              </Alert>
            ) : null}
          </SectionCard>

          <SectionCard
            title="Accesorii & badge-uri"
            subtitle="Bifează ce include produsul și evidențiază-l cu badge-uri."
            icon="A"
          >
            <Autocomplete
              multiple
              options={availableTags || []}
              value={selectedTags || []}
              onChange={(event, newValue) => onChangeTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    sx={{
                      borderColor: "rgba(148,163,184,0.4)",
                      color: "#E2E8F0",
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Vine însoțit"
                  placeholder="Selectează accesorii"
                  sx={glassInputStyles}
                />
              )}
              sx={{ mb: 2 }}
            />
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 700, color: "#fff" }}
            >
              Badge-uri
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {availableBadges?.length ? (
                availableBadges.map((badge) => {
                  const active = selectedBadges.includes(badge);
                  return (
                    <Chip
                      key={badge}
                      label={badge}
                      onClick={() => onToggleBadge(badge)}
                      variant={active ? "filled" : "outlined"}
                      sx={{
                        cursor: "pointer",
                        borderColor: "rgba(255,255,255,0.4)",
                        color: active ? "#0F172A" : "#E2E8F0",
                        background: active
                          ? "linear-gradient(120deg,#38BDF8,#2563EB)"
                          : "transparent",
                      }}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Nu există badge-uri disponibile.
                </Typography>
              )}
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="info"
                onClick={onStatusCheck}
                sx={{
                  borderColor: "rgba(59,130,246,0.5)",
                  color: "#BFDBFE",
                  borderRadius: 999,
                }}
              >
                Status Check
              </Button>
            </Stack>
          </SectionCard>

      <SectionCard
        title="Detalii produs"
        subtitle="Completează informațiile ce ajută autentificarea."
        icon="D"
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            Brand & Model
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderTextField("model", "Model")}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderTextField("brand", "Brand")}
            </Grid>
          </Grid>
        </Box>

        {isAccessoryWrap ? (
          renderAccessoryWrapDetails(accessoryVariant)
        ) : (
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    Logistică & Stare
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {renderTextField("culoareHW", "Culoare HW")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Locație"
                        value={details.location || ""}
                        onChange={(e) =>
                          onChangeProductDetail("location", e.target.value)
                        }
                        sx={glassInputStyles}
                      >
                        <MenuItem value="">
                          <em>Necompletat</em>
                        </MenuItem>
                        {locationOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Stare"
                        value="Pre-owned"
                        InputProps={{ readOnly: true }}
                        sx={glassInputStyles}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    Clasificare & Public
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Preț vânzare (RON)"
                        type="number"
                        value={salePrice || ""}
                        onChange={(e) => onChangeSalePrice?.(e.target.value)}
                        sx={glassInputStyles}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Preț de retail (MSRP)"
                        type="number"
                        value={details.msrpPrice || ""}
                        onChange={(e) =>
                          onChangeProductDetail("msrpPrice", e.target.value)
                        }
                        sx={glassInputStyles}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Categorie Prod"
                        value={details.categorieProd || ""}
                        onChange={(e) =>
                          onChangeProductDetail("categorieProd", e.target.value)
                        }
                        sx={glassInputStyles}
                      >
                        <MenuItem value="">
                          <em>Necompletat</em>
                        </MenuItem>
                        {categorieOptions.map((opt) => (
                          <MenuItem key={opt.key} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderTextField("subcategorie", "Subcategorie")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Pentru"
                        value={details.pentru || ""}
                        onChange={(e) =>
                          onChangeProductDetail("pentru", e.target.value)
                        }
                        sx={glassInputStyles}
                      >
                        <MenuItem value="">
                          <em>Necompletat</em>
                        </MenuItem>
                        {["Femei", "Barbati", "Copii", "Casa"].map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={wooCategoryOptions}
                        getOptionLabel={(option) =>
                          option?.label || option?.name || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={wooCategoriesValue}
                        onChange={(_event, newValue) =>
                          onChangeProductDetail(
                            "wooCategoriesSelected",
                            sanitizeWooCategoryList(newValue || [])
                          )
                        }
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option?.id || index}
                              label={option?.name || option?.label}
                              {...getTagProps({ index })}
                              sx={{
                                borderColor: "rgba(255,255,255,0.4)",
                                color: "#E2E8F0",
                                background: "rgba(15,23,42,0.35)",
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Categorii Woo"
                            placeholder="Selectează categorii Woo"
                            sx={glassInputStyles}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingWooCategories ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={16}
                                      sx={{ mr: 1 }}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    Dimensiuni & Mărimi
                  </Typography>
                  <Grid container spacing={2}>
                    {isFootwearCategory ? (
                      <>
                        <Grid item xs={12} md={6}>
                          {renderTextField("lungime", "Mărime încălțăminte")}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {renderTextField("inaltime", "Centimetri (cm)")}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {renderTextField(
                            "marimeDimensiune",
                            "Observații mărime"
                          )}
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} md={6}>
                          {renderTextField(
                            "marimeDimensiune",
                            "Mărime & Dimensiune"
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {renderTextField("lungime", "Lungime (cm)")}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {renderTextField("inaltime", "Înălțime (cm)")}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {renderTextField("adancime", "Adâncime (cm)")}
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    Culori & Materiale
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {renderTextField("culoareExterioara", "Culoare exterioară")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderTextField("culoareInterioara", "Culoare interioară")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderMaterialInputs("materialExterior", "Material exterior")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderMaterialInputs("materialInterior", "Material interior")}
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "rgba(255,255,255,0.85)",
                    }}
                  >
                    Identificatori
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {renderTextField("designerId", "Designer ID")}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderTextField("entrupyId", "Entrupy ID")}
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            )}
            {renderWooCommerceSection()}
          </SectionCard>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 999,
                px: 2,
                py: 0.5,
                background: "rgba(15,23,42,0.4)",
                border: "1px solid rgba(255,255,255,0.15)",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: !syncToPlatform ? "#FBBF24" : "rgba(255,255,255,0.6)",
                }}
              >
                Jira
              </Typography>
              <Switch
                checked={Boolean(syncToPlatform)}
                onChange={(e) => onToggleSyncDestination?.(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#38BDF8",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "rgba(56,189,248,0.5)",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: syncToPlatform ? "#34D399" : "rgba(255,255,255,0.6)",
                }}
              >
                Platforma
              </Typography>
            </Box>
            {fisaProdusStatus !== "CIORNA" && (
              <Button
                variant="outlined"
                color="success"
                onClick={onStatusCiorna}
                sx={{
                  borderColor: "rgba(34,197,94,0.5)",
                  color: "#BBF7D0",
                }}
              >
                Status Ciorna
              </Button>
            )}
            <Button
              variant="contained"
              type="button"
              sx={{
                background: "linear-gradient(120deg, #2563EB, #38BDF8)",
                px: 4,
                fontWeight: 600,
                boxShadow: "0 18px 35px rgba(37,99,235,0.45)",
                "&:hover": {
                  background: "linear-gradient(120deg, #1D4ED8, #0284C7)",
                },
              }}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </Stack>
          {wooSyncStatus?.message && (
            <Alert
              severity={wooSyncStatus.severity || "success"}
              onClose={onClearWooSyncStatus}
              sx={{
                mt: 2,
                background: "rgba(15,118,110,0.18)",
                border: "1px solid rgba(45,212,191,0.3)",
                color: "#ECFEFF",
                fontWeight: 500,
              }}
            >
              {wooSyncStatus.message}
            </Alert>
          )}
        </Stack>
      </CardContent>
      </Card>
      {effectiveWooLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            borderRadius: 6,
            background: "rgba(15,23,42,0.55)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" />
            <Typography sx={{ color: "#E0F2FE", fontWeight: 600 }}>
              Se încarcă datele din WooCommerce...
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FisaProdusCard;
export { FisaProdusCard };
